import { JsonValue } from '@angular-devkit/core/src/json/utils';

export interface StringMap<Value = string> {
  [key: string]: Value;
}

export class StringUtil {
  static capitalizeFirstLetter(text: string) {
    if (!text) {
      return '';
    }
    return text.substring(0, 1).toUpperCase() + text.substring(1);
  }

  static contentAsParsedHtml(unparsedHtml): string {
    const html = document.createElement('html');
    html.innerHTML = unparsedHtml;
    const content = document.createElement('div');
    content.innerHTML = html.getElementsByTagName('body')[0].innerHTML;
    const filteredStyles = html.getElementsByTagName('style')[0];
    if (filteredStyles !== undefined) {
      filteredStyles.innerHTML = filteredStyles.innerText.replace(/body\s?\{[^}]*\}/, '');
      content.appendChild(filteredStyles);
    }
    return content.innerHTML || '';
  }

  static getKeyFromEncodedQueryString(encoded: string, key: string, URIComponent = false): string {
    const decoded = URIComponent ? decodeURI(encoded) : decodeURIComponent(encoded);
    const uriParts = decoded?.split('?');
    const queryParams = uriParts?.length > 1 ? uriParts[1] : '';
    const keyValuePairs = queryParams?.split('&')?.reduce((kvPairs, kvPair) => {
      const parts = kvPair.split('=');
      if (parts?.length > 1) {
        kvPairs[parts[0]] = parts[1];
      }
      return kvPairs;
    }, {});
    return keyValuePairs?.[key] ?? '';
  }
}

export function matchesInAppLink(uri, environmentConfig): boolean {
  if (!uri || !uri.includes('http')) {
    // just return the uri if it can't be parsed as a URL
    return uri;
  }
  const url = new URL(uri);
  const hostBase = 'kumanu.com';
  const purposefulEnvHostname = `${environmentConfig.environment}.purposeful.${hostBase}`;
  const resourcefulEnvHostname = `${environmentConfig.environment}.resourceful.${hostBase}`;
  const purposefulHostname = `purposeful.${hostBase}`;
  const resourcefulHostname = `resourceful.${hostBase}`;
  // inAppLinks either match the hostname, a purposeful/resourceful hostname (environment), or one of the prod hostnames
  return [
    environmentConfig.hostname,
    purposefulEnvHostname,
    resourcefulEnvHostname,
    purposefulHostname,
    resourcefulHostname,
  ].some((uriPart) => url?.hostname === uriPart);
}

export function getInAppLink(uri): string {
  if (!uri || !uri.includes('http')) {
    // just return the uri if it can't be parsed as a URL
    return uri;
  }
  const url = new URL(uri);
  return `${url.pathname}${url.search}`;
}

export function getQueryParamsFromString(queryParamsString: string): Record<string, any> {
  // remove '?' if exists as first char of string
  const cleanedString = queryParamsString?.[0] === '?' ? queryParamsString.substring(1) : queryParamsString;
  const queryParams = cleanedString
    ?.split('&') // split into query param key/value pairs
    ?.reduce((aggregator, pairs) => {
      // split key & value
      const [key, ...values] = pairs.split('=');
      // assign to object if key is truthy
      if (key) {
        // rejoin the values back together, only want to split on the first instance of `=`
        aggregator[key] = values?.join('=');
      }
      return aggregator;
    }, {} as any);
  return queryParams || {};
}

export function isJSON(str: string): boolean {
  if (typeof str !== 'string') {
    return false;
  }
  try {
    const json = JSON.parse(str);
    // will return true if successfully parses and is a validly parsed item (object, array, or parsed primitive value)
    return ['object', 'boolean', 'string', 'number'].includes(typeof json) || Array.isArray(json);
  } catch (e) {
    /* intentionally left blank */
  }
  return false;
}

export function tryParseJSON(json: string): JsonValue {
  if (!isJSON(json)) {
    return false;
  }
  return JSON.parse(json);
}
